import { io, Socket } from 'socket.io-client'
import type {
  VaSnapshotPayload,
  VirtualAgentSummary,
  ActiveCall,
  VaLanguage,
  CallStatus,
  AgentStatus,
  VaFilters,
} from 'shared/src'
import { useAlerts } from '@/composables/alert/useAlerts'
import { alertEnum } from '@/enums/AlertEnum'
import { useLivekitCore } from '@/composables/livekit'

export interface ActiveCallWithDuration extends ActiveCall {
  durationSec: number
}
export interface VirtualAgentSummaryWithDuration extends Omit<VirtualAgentSummary, 'activeCalls'> {
  activeCalls: ActiveCallWithDuration[]
}

const agents = ref<Record<string, VirtualAgentSummaryWithDuration>>({})
const availableFilters = ref<{
  languages: VaLanguage[]
  statuses: CallStatus[]
  agents: VirtualAgentSummary[]
  agentsStatuses: AgentStatus[]
}>({
  languages: [],
  statuses: [],
  agents: [],
  agentsStatuses: [],
})

const activeFilters = ref<VaFilters>({})
const isLoading = ref(true)
const isConnected = ref(false)
const error = ref<{ message: string; code: string } | null>(null)

let socket: Socket | null = null
// Use a unique symbol to survive HMR reloads
const initKey = Symbol.for('va-socket-initialized')
type GlobalWithInit = Record<symbol, boolean>
const getInitialized = (): boolean => (globalThis as unknown as GlobalWithInit)[initKey] || false
const setInitialized = (value: boolean) =>
  ((globalThis as unknown as GlobalWithInit)[initKey] = value)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

const addDurations = (
  payload: VaSnapshotPayload,
): Record<string, VirtualAgentSummaryWithDuration> => {
  const now = Date.now()
  return Object.fromEntries(
    payload.virtualAgents.map((va) => [
      va.id,
      {
        ...va,
        activeCalls: va.activeCalls.map((call) => ({
          ...call,
          durationSec: Math.max(0, Math.floor(((call.endTime ?? now) - call.startTime) / 1000)),
        })),
      },
    ]),
  )
}

const mergeAgents = (payload: VaSnapshotPayload) => {
  const updated = addDurations(payload)
  for (const [id, data] of Object.entries(updated)) {
    agents.value[id] = data
  }
}

export const useAgentsRealtime = () => {
  const { addAlert } = useAlerts()

  const refresh = () => {
    if (!socket) return
    socket.emit('va:filter', activeFilters.value)
    isLoading.value = true
  }

  const applyFilters = (filters: VaFilters) => {
    activeFilters.value = filters

    // Debounce refresh to avoid excessive API calls
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      refresh()
      debounceTimer = null
    }, 300) // 300ms debounce
  }

  onMounted(() => {
    if (getInitialized()) {
      return
    }
    setInitialized(true)

    socket = io('/va', {
      path: '/va/socket.io',
      transports: ['websocket'],
      withCredentials: true,
    })

    socket.on('connect', () => {
      isConnected.value = true
      // Filters will be emitted when useLiveVirtualAgents calls applyFilters
    })

    socket.on('disconnect', () => {
      isConnected.value = false
      isLoading.value = true
    })

    socket.on(
      'va:snapshot',
      (
        payload: VaSnapshotPayload & {
          filters?: {
            languages: VaLanguage[]
            statuses: CallStatus[]
            agents: VirtualAgentSummary[]
            agentsStatuses: AgentStatus[]
          }
        },
      ) => {
        agents.value = addDurations(payload)
        if (payload.filters) availableFilters.value = payload.filters

        isLoading.value = false
        error.value = null // Clear error on successful snapshot
      },
    )

    socket.on('va:update', (payload: VaSnapshotPayload) => {
      mergeAgents(payload)
    })

    socket.on('va:error', (err: { message: string; code: string }) => {
      console.error('[WebSocket Error]', err.code, ':', err.message)
      error.value = err
      isLoading.value = false
      addAlert(err.message, alertEnum.Danger)
    })

    window.setInterval(() => {
      const { disconnectedRooms } = useLivekitCore()
      const now = Date.now()

      for (const va of Object.values(agents.value)) {
        va.activeCalls.forEach((call) => {
          // Stop updating duration if the specific room has disconnected
          if (disconnectedRooms.value.has(call.roomName)) {
            return
          }

          // Only update duration if call hasn't ended
          if (!call.endTime) {
            call.durationSec = Math.max(0, Math.floor((now - call.startTime) / 1000))
          }
        })
      }
    }, 1000)
  })

  const agentsList = computed(() => Object.values(agents.value))

  return {
    agents,
    agentsList,
    availableFilters,
    activeFilters,
    applyFilters,
    isLoading,
    isConnected,
    refresh,
  }
}
